import cn from 'classnames';
import { ChangeEvent, ReactNode, useState } from 'react';
import './index.scss';

interface Props {
  id?: string;
  className?: string;
  inputClassName?: string;
  label?: string | ReactNode;
  disabled?: boolean;
  eyePassword?: boolean;
  requirements?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  requirementsLabels?: {
    minChars: string;
    charCases: string;
    numbers: string;
    specials: string;
  };
  value?: string;
}

export const PasswordInput = ({
  id,
  className,
  inputClassName,
  label,
  disabled = false,
  eyePassword = false,
  requirements = false,
  requirementsLabels = {
    minChars: 'Min 8 characters',
    charCases: 'Upper and lower cases',
    numbers: 'Numbers',
    specials: 'Special characters',
  },
  onChange = () => {},
  value,
}: Props) => {
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState(value || '');

  const _onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    onChange(event);
  };

  return (
    <div className={cn(className, 'PasswordInput')}>
      {label && (
        <div className="PasswordInput__Label">
          {typeof label === 'string' ? (
            <label className="PasswordInput__Label__DefaultLabel">
              {label}
            </label>
          ) : (
            label
          )}
        </div>
      )}
      <div className={cn(inputClassName, 'PasswordInput__Input')}>
        <input
          id={id}
          data-testid={id}
          onChange={_onChange}
          value={value === undefined ? password : value}
          type={showPassword ? 'text' : 'password'}
          disabled={disabled}
          className="form-control"
        />
        {eyePassword && (
          <i
            className={cn(
              'PasswordInput__EyeIcon',
              'mdi',
              showPassword ? 'mdi-eye' : 'mdi-eye-off'
            )}
            onMouseDown={() => setShowPassword(true)}
            onMouseUp={() => setShowPassword(false)}
            onMouseLeave={() => setShowPassword(false)}
          />
        )}
      </div>
      {requirements && (
        <div className="PasswordInput__Requirements">
          <div
            className={cn({
              PasswordInput__Requirements__Disabled: password.length < 8,
            })}
          >
            <i className="PasswordInput__Requirements__Icon mdi mdi-check" />
            {requirementsLabels.minChars}
          </div>
          <div
            className={cn({
              PasswordInput__Requirements__Disabled: !password.match(
                /^(?=.*[a-z])(?=.*[A-Z]).*$/
              ),
            })}
          >
            <i className="PasswordInput__Requirements__Icon mdi mdi-check" />
            {requirementsLabels.charCases}
          </div>
          <div
            className={cn({
              PasswordInput__Requirements__Disabled:
                !password.match(/^(?=.*[0-9]).*$/),
            })}
          >
            <i className="PasswordInput__Requirements__Icon mdi mdi-check" />
            {requirementsLabels.numbers}
          </div>
          <div
            className={cn({
              PasswordInput__Requirements__Disabled:
                !password.match(/^(?=.*\W).*$/),
            })}
          >
            <i className="PasswordInput__Requirements__Icon mdi mdi-check" />
            {requirementsLabels.specials}
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordInput;
